'use client';

import { createContext, useContext, useMemo } from 'react';
import {
  ClassificationResource,
  ClassificationVersionResource,
  ClassificationVersionSummaryResource,
} from '@/libs/data-access/klass';

export type ResolvedVersion = NonNullable<ClassificationResource['versions']>[number];

type BaseVersionContextType = {
  classification: ClassificationResource;
  versionSummary: ClassificationVersionSummaryResource;
};

export type VersionContextType = BaseVersionContextType & {
  versionResource?: ClassificationVersionResource;
};

const BaseVersionContext = createContext<BaseVersionContextType | null>(null);
const VersionResourceContext = createContext<ClassificationVersionResource | null>(null);

export function VersionProvider({
  classification,
  versionSummary,
  children,
}: Readonly<{
  classification: ClassificationResource;
  versionSummary: ClassificationVersionSummaryResource;
  children: React.ReactNode;
}>) {
  const value = useMemo(() => ({ classification, versionSummary }), [classification, versionSummary]);

  return <BaseVersionContext.Provider value={value}>{children}</BaseVersionContext.Provider>;
}

export function VersionResourceLayer({
  versionResource,
  children,
}: Readonly<{
  versionResource?: ClassificationVersionResource;
  children: React.ReactNode;
}>) {
  if (!versionResource) return <>{children}</>;
  return <VersionResourceContext.Provider value={versionResource}>{children}</VersionResourceContext.Provider>;
}
export function useVersion(): VersionContextType {
  const base = useContext(BaseVersionContext);
  if (!base) throw new Error('useVersion must be used within VersionProvider');

  const versionResource = useContext(VersionResourceContext) ?? undefined;
  return { ...base, versionResource };
}
